import { useMutation, useQuery, useQueryClient, UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { TResponse } from "../../../types/common";
import { IPageBannerPayload, IPageBannerResponse } from "../../../types/api/pageBanner";
import { savePageBanner, detailPageBanner } from "./callbacks";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";

/* ---------------------- Save Page Banner ---------------------- */
export const usePageBannerSave = (
  {
    onSuccess,
    onError,
  }: {
    onSuccess?: (res: TResponse<IPageBannerResponse>) => void;
    onError?: (error: unknown) => void;
  },
  options: UseMutationOptions<TResponse<IPageBannerResponse>, unknown, IPageBannerPayload> = {}
) => {
  const queryClient = useQueryClient();

  return useMutation<TResponse<IPageBannerResponse>, unknown, IPageBannerPayload>({
    mutationFn: async (data: IPageBannerPayload) => {
      const response = await savePageBanner(data);
      return response.data;
    },

    onSuccess: (res, ...rest) => {
      if (res.data?.pageType) {
        queryClient.invalidateQueries({
          queryKey: [API_ENDPOINTS.PAGE_BANNER_DETAIL, res.data.pageType],
        });
      }
      onSuccess?.(res);
      options?.onSuccess?.(res, ...rest);
    },

    onError: (error, ...rest) => {
      onError?.(error);
      options?.onError?.(error, ...rest);
    },

    ...options,
  });
};

/* ---------------------- Page Banner Detail ---------------------- */
export const usePageBannerDetail = (
  pageType: string,
  options?: Omit<UseQueryOptions<TResponse<IPageBannerResponse>, Error>, "queryKey" | "queryFn">
) => {
  return useQuery<TResponse<IPageBannerResponse>, Error>({
    queryKey: [API_ENDPOINTS.PAGE_BANNER_DETAIL, pageType],

    queryFn: async () => {
      const res = await detailPageBanner(pageType);
      return res.data;
    },

    enabled: !!pageType,
    refetchOnWindowFocus: false,
    staleTime: 300000, // 5 min

    ...options,
  });
};